import { AppShape } from "@eave/shared/types/api";

import {
  fetchWorksheets,
  getWsById,
  getWsByName,
  updateLoanFileWorksheet,
} from "underwriter_dashboard/shared/WorksheetEditor/wsHelpers";
import { WS_NAME } from "underwriter_dashboard/shared/WorksheetEditor/wsDefs";

import APIClient from "../../../APIClient";
import { CarbonInputGenerationParams } from "../../../shared/carbon-types";

const prepareUpdatesForAPI = (updates, modalFields: CarbonInputGenerationParams[]) =>
  Object.keys(updates).map(key => {
    let value = updates[key];

    const fieldDef = modalFields.find(field => field.name === key);
    if (fieldDef.prepareForAPI) {
      value = fieldDef.prepareForAPI(value);
    }

    return {
      name: key,
      value,
    };
  });

const createPrepaidsWorksheet = (loanFileWs, appId) => {
  // 1. create it, and
  // 2. attach it to the loan_file worksheet
  // 1. creation
  const creationPayload = {
    application_id: appId,
    worksheet_name: WS_NAME.PREPAIDS,
  };

  return APIClient.post(`/worksheets`, creationPayload).then(({ worksheet }) => {
    // 2. attach to loan_file ws

    const { id: prepaidsId } = worksheet;

    return updateLoanFileWorksheet(loanFileWs, [
      {
        name: "prepaids",
        value: prepaidsId,
      },
    ]);
  });
};

const createPrepaidsWorksheetIfNeeded = (worksheets, app) => {
  const prepaidsWs = getWsByName(worksheets, WS_NAME.PREPAIDS);
  if (prepaidsWs) {
    return Promise.resolve(worksheets);
  }

  const loanFileWs = getWsByName(worksheets, WS_NAME.LOAN_FILE);
  if (!loanFileWs) {
    // should never happen, as loan_file worksheet is created on loan application creation.
    throw new Error(`could not load loan_file worksheet on app ${app.id}`);
  }

  return createPrepaidsWorksheet(loanFileWs, app.id).then(() => fetchWorksheets(app));
};

export const fetchWorksheetPrepaids = (app: AppShape) => {
  return fetchWorksheets(app)
    .then(worksheets => createPrepaidsWorksheetIfNeeded(worksheets, app))
    .then(worksheets => {
      const prepaidsWs = getWsByName(worksheets, WS_NAME.PREPAIDS);

      if (!prepaidsWs || !prepaidsWs.instance) {
        throw new Error(`could not load prepaids worksheet on app ${app.id}`);
      }

      const items = prepaidsWs.instance.items.map((item, i) => {
        const itemWs = getWsById(worksheets, item);
        if (!itemWs) {
          // this means:
          // the prepaids ws lists a prepaid item ws that didn't get loaded.
          // a bad data situation. should never happen.
          throw new Error(
            `prepaid item worksheet '${item}' not found on prepaids worksheet ${prepaidsWs.id}`,
          );
        }

        // store original array index explicitly

        return {
          ...itemWs.instance,
          array_index: i, // match ws field snake casing
        };
      });

      return {
        id: prepaidsWs.id,
        version: prepaidsWs.instance.version,
        items,
      };
    });
};

export const updatePrepaid = (id, version, formUpdates, modalFields) => {
  if (!formUpdates) {
    // no updates -- nothing to do.
    return Promise.resolve(null);
  }

  const updates = prepareUpdatesForAPI(formUpdates, modalFields);
  const payload = {
    version,
    updates,
  };

  return APIClient.post(`/worksheets/${id}`, payload);
};

export const createPrepaid = (app: AppShape, prepaids, formUpdates, modalFields) => {
  // worksheet array item creation flow is:
  // 1. creation: create new array item (ie here, a prepaid_item)
  // 2. append: append it to the (parent worksheet) array (ie here, prepaids)
  // 3. update: add values to that new array item (ie here, the prepaid_item from step 1)

  // 1. creation
  const creationPayload = {
    application_id: app.id,
    worksheet_name: WS_NAME.PREPAID_ITEM,
  };

  let newPrepaidId;
  let newPrepaidVersion;

  return APIClient.post(`/worksheets`, creationPayload)
    .then(({ worksheet }) => {
      // 2. append

      newPrepaidId = worksheet.id;
      newPrepaidVersion = worksheet.version;

      const { id: prepaidsId, version: prepaidsVersion } = prepaids;
      const appendPayload = {
        version: prepaidsVersion,
        updates: [
          {
            name: "items",
            value: newPrepaidId,
          },
        ],
      };

      return APIClient.post(`/worksheets/${prepaidsId}`, appendPayload);
    })
    .then(() => {
      // 3. update
      return updatePrepaid(newPrepaidId, newPrepaidVersion, formUpdates, modalFields);
    });
};

export const deletePrepaid = (prepaids, formState) => {
  const { id: prepaidsId } = prepaids;
  const { version, array_index: index } = formState;

  const payload = {
    version,
    updates: [
      {
        name: "items",
        action: "delete",
        index,
      },
    ],
  };

  return APIClient.post(`/worksheets/${prepaidsId}`, payload);
};

export const importPrepaids = (prepaids, app: AppShape) => {
  const payload = {
    worksheet_id: prepaids.id,
  };
  return APIClient.post(`/uw/${app.id}/worksheets/populate`, payload);
};
